/* eslint-disable operator-linebreak */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Select, Input, Checkbox, Form } from 'antd';
import { WebButton } from 'src/shared/styled';
import useGetQueryString from 'src/shared/hooks/useGetQueryString';
import {
  Wrapper,
  Content,
  SearchToolBox,
  FullScreenSelect,
  ConditionList,
  OperatorSelect,
  StructureSelect,
  FieldSelect,
  BooksContent,
} from './Styled';
import {
  DEFAULT_CONDITION,
  defaultSearchField,
  PAGE_SIZE,
  OPERATOR,
  orderTypes,
  OPERATOR_LIST,
  STRUCTURE_LIST,
  FIELD_LIST,
} from './Constants';
import {
  getBooksApi,
  getBookRecordGroupsApi,
  getInventoriesApi,
} from './ApiService';
import BookList from './BookList';

const { Option } = Select;

const defaultSortOrder = {
  ...defaultSearchField,
  Field: 'date',
  Operator: 'or',
  SortType: 'dsc',
  Value: '0',
};

function BookSearch() {
  const { Searchkey } = useGetQueryString();
  const [books, setBooks] = useState([]);
  const [bookLoading, setBookLoading] = useState(false);
  const [recordGroups, setRecordGroups] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);

  const formRef = React.useRef(null);
  const initialFormValues = useMemo(
    () => ({
      selectedGroup: 0,
      selectedInventory: 0,
      currentSortOrder: 'date,dsc',
      ebookSearch: false,
      conditionList: [{ ...DEFAULT_CONDITION, Value: Searchkey ?? '' }],
    }),
    [Searchkey]
  );

  const getBooks = useCallback(
    (page: any) => {
      const {
        conditionList,
        selectedGroup,
        selectedInventory,
        ebookSearch,
        currentSortOrder,
      } = (formRef.current as any).getFieldsValue(true);
      const [field, sortType] = currentSortOrder.split(',');
      const params = [
        ...conditionList.filter(({ Value }: any) => Value),
        { ...defaultSortOrder, Field: field, SortType: sortType },
      ];
      setBookLoading(true);
      getBooksApi({
        pageSize: PAGE_SIZE,
        page,
        params,
        selectedGroup,
        selectedInventory,
        ebookSearch,
      })
        .then((data) => {
          setBookLoading(false);
          if (!data.length || !data[0].Success) {
            setTotalBooks(0);
            setBooks([]);
            return;
          }

          setBooks(data);
          setTotalBooks(data[0].TotalRecords);
        })
        .catch(() => {
          setBookLoading(false);
        });
    },
    [formRef.current]
  );

  useEffect(() => {
    getBookRecordGroupsApi().then((data) => {
      setRecordGroups([{ ID: 0, Name: 'Bộ sưu tập: Tất cả' }, ...data] as any);
    });

    getInventoriesApi().then((data) => {
      setInventories([
        { InventoryID: 0, InventoryName: 'Kho: Tất cả' },
        ...data,
      ] as any);
    });

    getBooks(currentPage);
  }, []);

  const handleSearch = useCallback(() => {
    getBooks(1);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: any) => {
    getBooks(page);
    setCurrentPage(page);
  }, []);

  const handleAddCondition = useCallback(() => {
    if (!formRef.current) return;

    (formRef.current as any).setFieldsValue({
      conditionList: [
        ...(formRef.current as any).getFieldValue('conditionList'),
        { ...DEFAULT_CONDITION, Operator: OPERATOR.and },
      ],
    });
  }, [
    formRef.current
  
  ]);

  const handleKeyDown = (e: any) => {
    if (e.code === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Wrapper>
      <Content>
        <Form
          ref={formRef}
          initialValues={initialFormValues}
          onFinish={handleSearch}
        >
          <SearchToolBox onKeyDown={handleKeyDown}>
            <Form.Item name="selectedGroup">
              <FullScreenSelect>
                {recordGroups.map(({ ID, Name }) => (
                  <Option key={ID} value={ID}>
                    {Name}
                  </Option>
                ))}
              </FullScreenSelect>
            </Form.Item>
            <Form.Item name="selectedInventory">
              <FullScreenSelect>
                {inventories.map(({ InventoryID, InventoryName }) => (
                  <Option key={InventoryID} value={InventoryID}>
                    {InventoryName}
                  </Option>
                ))}
              </FullScreenSelect>
            </Form.Item>
            <Form.Item name="currentSortOrder">
              <FullScreenSelect>
                {orderTypes.map(({ id, name }) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                ))}
              </FullScreenSelect>
            </Form.Item>
            <Form.Item name="ebookSearch" valuePropName="checked">
              <Checkbox>Tìm file</Checkbox>
            </Form.Item>
            <ConditionList>
              <table className="table">
                <tbody>
                  <Form.List name="conditionList">
                    {(fields, { remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <tr key={field.key}>
                            <td width={120}>
                              {index ? (
                                <Form.Item name={[index, 'Operator']}>
                                  <OperatorSelect>
                                    {OPERATOR_LIST.map(({ id, name }) => (
                                      <Option key={id} value={id}>
                                        {name}
                                      </Option>
                                    ))}
                                  </OperatorSelect>
                                </Form.Item>
                              ) : (
                                ''
                              )}
                            </td>
                            <td>
                              <Form.Item name={[index, 'Value']}>
                                <Input
                                  placeholder="Nhập từ khóa tìm kiếm"
                                  style={{ width: '100%', height: 'auto' }}
                                />
                              </Form.Item>
                            </td>
                            <td width={100}>
                              <Form.Item name={[index, 'Structure']}>
                                <StructureSelect>
                                  {STRUCTURE_LIST.map(({ id, name }) => (
                                    <Option key={id} value={id}>
                                      {name}
                                    </Option>
                                  ))}
                                </StructureSelect>
                              </Form.Item>
                            </td>
                            <td width={140}>
                              <Form.Item name={[index, 'Field']}>
                                <FieldSelect>
                                  {FIELD_LIST.map(({ id, name }: any) => (
                                    <Option key={id} value={id}>
                                      {name}
                                    </Option>
                                  ))}
                                </FieldSelect>
                              </Form.Item>
                            </td>
                            <td width={65}>
                              {!!index && (
                                <Button
                                  type="link"
                                  className="ant-btn-dangerous"
                                  onClick={() => remove(index)}
                                >
                                  <i
                                    className="fa fa-times "
                                    aria-hidden="true"
                                  />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </Form.List>
                </tbody>
              </table>
              <Button type="link" onClick={handleAddCondition}>
                <i className="fa fa-plus-circle" aria-hidden="true" /> Thêm điều
                kiện
              </Button>
            </ConditionList>
            <WebButton type="primary" htmlType="submit">
              Tìm Kiếm
            </WebButton>
          </SearchToolBox>
        </Form>
        <BooksContent>
          <BookList
            bookList={books}
            isLoading={bookLoading}
            total={totalBooks}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
          />
        </BooksContent>
      </Content>
    </Wrapper>
  );
}

export default BookSearch;
